<template>
    <div class="flex flex-col h-full gap-2 p-2">
        <ai-grid-edit
            ref="eoruGrid"
            :action="action"
            :args="args"
            :dots-click="dotsClick"
            :hasntRoute="true"
            :lockedRows="lockedRows"
            :meta="meta"
            @userDataChanged="onUserDataChanged"
        />
    </div>
</template>
<script setup>
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';

const props = defineProps({
    action: {
        type: Function,
        default: null,
    },
    userData: {
        type: Object,
        default: () => {
            return {};
        },
    },
});

const route = useRoute();
const eoruGrid = ref();
const args = {
    select: {
        select_args: {
            eobj_id: route.params.id,
        },
    },
    save: {
        insert_args: {
            parent_id: route.params.id,
        },
    },
};
const meta = {
    keyExpr: 'eobj_id',
    viewId: 'widgetEoru',
    columns: [
        {
            dataField: 'dnum',
            caption: 'Диспетчерский №',
            width: 150,
        },
        {
            dataField: 'bal_id',
            caption: 'Балансовая принадлежность',
            width: 150,
            dataType: 'number',
            editor: { type: 'ai-popup', text: 'bname' },
            component: {
                id: 'kodp',
                text: 'name',
                path: 'components/dictionary-components/ai-dictionary-virtual-grid.vue',
                saveButtonVisible: false,
                title: 'Выбор балансовой принадлежности',
                meta: {
                    columns: [
                        { field: 'kodp', text: 'kodp', visible: false },
                        { field: 'enscope_name', text: 'enscope_name', visible: false },
                        { field: 'sname', text: 'Код', width: 100 },
                        { field: 'name', text: 'Наименование', width: 250 },
                        { field: 'kod_separator', text: 'kod_separator', visible: false },
                        { field: 'kod_ecls', text: 'kod_ecls', visible: false },
                        { field: 'kod_parent', text: 'kod_parent', visible: false },
                        { field: 'inn', text: 'ИНН', width: 100 },
                        { field: 'kpp', text: 'КПП', width: 100 },
                        { field: 'addr', text: 'Адрес юридический' },
                        { field: 'pr_active', text: 'pr_active', data_type: 'boolean', visible: false },
                    ],
                    loadUrl: 'e-grid./bal/data/get',
                    keyExpr: 'kodp',
                    autoLoadData: false,
                    toRemoveToolbarItemsNames: ['copy', 'edit', 'add', 'delete', 'save'],
                },
            },
        },
        {
            dataField: 'bkod_separator',
            width: 150,
            visible: false,
            dataType: 'number',
            allowEditing: false,
        },
        {
            dataField: 'bkod_ecls',
            width: 150,
            visible: false,
            dataType: 'number',
            allowEditing: false,
        },
        {
            dataField: 'bname',
            width: 150,
            visible: false,
            allowEditing: false,
        },
        {
            dataField: 'expl_id',
            caption: 'Эксплуатационная ответственность',
            width: 150,
            dataType: 'number',
            editor: { type: 'ai-popup', text: 'ename' },
            component: {
                id: 'kodp',
                text: 'name',
                saveButtonVisible: false,
                title: 'Выбор эксплуатационной ответственности',
                path: 'views/e-grid/eosrc-card/field-wrappers/component-ge-expl-id-vg.vue',
            },
        },
        {
            dataField: 'ename',
            width: 150,
            visible: false,
            allowEditing: false,
        },
        {
            dataField: 'kod_v',
            caption: 'Напряжение, кВ',
            width: 150,
            editor: {
                type: 'ai-drop-down',
                text: 'sv',
            },
            component: {
                path: 'components/ai-grid-ro/ai-grid-ro.vue',
                text: 'sv',
                meta: {
                    keyExpr: 'kod_v',
                    columns: [
                        { caption: 'kod_v', visible: false, dataField: 'kod_v' },
                        {
                            caption: 'Напряжение',
                            dataField: 'sv',
                        },
                    ],
                    api: { select: 'e-grid./hkv/get' },
                },
                args: {
                    select_args: { schema: 'e_grid', table: 'hk_v', order: 'nv' },
                },
            },
        },
        {
            dataField: 'tech_location_code',
            caption: 'Код технического места оборудования в SAP (или ее аналога)',
            width: 150,
            visible: false,
        },
        {
            dataField: 'imported',
            caption: 'Импортировано',
            width: 150,
            dataType: 'boolean',
            allowEditing: false,
        },
        {
            dataField: 'coment',
            caption: 'Комментарий',
        },
        {
            dataField: 'kindname',
            caption: 'Вид оборудования',
            width: 150,
            visible: false,
            allowEditing: false,
        },
        {
            dataField: 'parent_id',
            width: 150,
            visible: false,
            allowEditing: false,
        },
        {
            dataField: 'deleted',
            width: 150,
            visible: false,
            dataType: 'boolean',
            allowEditing: false,
        },
    ],
    api: {
        select: 'e-grid./eoru/get',
        save: 'e-grid./eoru/set',
    },
    hasntRoute: true,
};
const dotsClick = metaCell => {
    if (metaCell.cellInfo.column.dataField !== 'expl_id') return;
    const changes = eoruGrid.value.gridInstance.option('editing.changes');
    if (Array.isArray(changes) && changes.length > 0) {
        const changesRow = changes.find(item => item.key === metaCell.cellInfo.key);
        if (changesRow) {
            return changesRow.data;
        }
    }
    return metaCell.cellInfo.data;
};

const onUserDataChanged = async e => {
    const gridChanges = e.changes.gridChanges;
    let isUpdated = false;
    const columns = eoruGrid.value?.gridInstance.option('columns');
    const balIdCellTemplate = columns.find(col => col.dataField === 'bal_id').editCellTemplate;
    const explIdCellTemplate = columns.find(col => col.dataField === 'expl_id').editCellTemplate;
    gridChanges
        .filter(item => item.type === 'insert')
        .forEach(item => {
            if (!item.data.bal_id) {
                Object.assign(item.data, {
                    bal_id: props.userData.bal_id,
                    bname: props.userData.bname,
                    bkod_separator: props.userData.bkod_separator,
                });
                //если столбец использует cellTemplate, добавленное программно значение так же добавляем в DS шаблона
                if (balIdCellTemplate) {
                    eoruGrid.value?.addCellTemplateValue(
                        'bal_id',
                        'bal_id',
                        props.userData.bal_id,
                        'bname',
                        props.userData.bname
                    );
                }
                isUpdated = true;
            }
            if (!item.data.expl_id) {
                Object.assign(item.data, {
                    expl_id: props.userData.expl_id,
                    ename: props.userData.ename,
                });
                if (explIdCellTemplate) {
                    eoruGrid.value?.addCellTemplateValue(
                        'expl_id',
                        'expl_id',
                        props.userData.expl_id,
                        'ename',
                        props.userData.ename
                    );
                }
                isUpdated = true;
            }
        });
    if (isUpdated) {
        eoruGrid.value?.setUserDataChanges(gridChanges);
    }
};
const lockedRows = ref([]);
onMounted(() => {
    setTimeout(() => {
        lockedRows.value = eoruGrid.value?.gridInstance
            .option('dataSource')
            .filter(row => row['imported'] === true)
            .map(el => el[meta.keyExpr]);
    }, 500);
});
defineExpose({
    ref: eoruGrid,
});
</script>
